@layer branded-header {
  .header {
    display: flex;
    width: 100%;
    min-height: 58px;
    padding: var(--n-branded-header-padding, 1rem 1.5rem);
    box-sizing: border-box;
    background-color: var(--n-branded-header-bg-color, transparent);
    color: var(--n-branded-header-text-color, inherit);
  }

  .inner {
    position: relative;
    width: 100%;
    max-inline-size: var(--n-branded-header-max-inline-size, 60rem);
    margin-inline: auto;
    display: flex;
    align-items: center;
  }

  .inner[data-layout='STACKED'] {
    flex-direction: column;
    gap: 0.75rem;
  }

  .inner[data-layout='INLINE'] {
    flex-direction: row;
    gap: 1.5rem;
  }

  .logoSlot {
    flex-shrink: 0;
  }

  /* STACKED is a column flex container with non-stretch align-items, so shrink
     (main axis = vertical there) can't bound the mark's width — only an explicit
     max-inline-size lets the ellipsis engage. */
  .brandMarkSlot {
    flex-shrink: 1;
    min-width: 0;
    max-inline-size: 100%;
  }

  /* The mobile menu button is absolutely positioned, so it adds no width to the
     flex line and centered content would run under it. Reserve its width
     (--n-scale-800, the sm icon button) at both edges to keep the centering
     symmetric. */
  .inner[data-layout='MOBILE'][data-has-menu] .brandMarkSlot {
    max-inline-size: calc(100% - 2 * var(--n-scale-800));
  }

  .brandMark {
    display: block;
    min-width: 0;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .inner[data-layout='STACKED'][data-logo-align='CENTER'] {
    align-items: center;
  }

  .inner[data-layout='STACKED'][data-logo-align='LEFT'] {
    align-items: flex-start;
  }

  .inner[data-layout='STACKED'][data-logo-align='RIGHT'] {
    align-items: flex-end;
  }

  .inner[data-layout='INLINE'] .logoSlot {
    align-self: center;
  }

  .navSlot {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 1.5rem;
  }

  .inner[data-nav-align='LEFT'] .navSlot {
    justify-content: flex-start;
  }

  .inner[data-nav-align='CENTER'] .navSlot {
    justify-content: center;
  }

  .inner[data-nav-align='RIGHT'] .navSlot {
    justify-content: flex-end;
  }

  .inner[data-nav-align='SPREAD'] .navSlot {
    align-self: stretch;
  }

  .inner[data-nav-align='SPREAD'] .navList {
    flex: 1;
    justify-content: space-between;
  }

  .inner[data-layout='INLINE'] .navSlot {
    flex: 1;
  }

  .inner[data-layout='STACKED'] .navSlot {
    width: 100%;
  }

  .header[data-fixed] {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .logoLink {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
  }

  .logoImg {
    max-height: var(--n-branded-header-logo-max-height, 2.5rem);
    width: auto;
    object-fit: contain;
    display: block;
  }

  .navList {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: inherit;
  }

  .navItem {
    display: block;
  }

  .navLink {
    color: inherit;
    text-decoration: none;
    font-size: 0.875rem;
    white-space: nowrap;
  }

  .navLink:hover {
    text-decoration: underline;
  }

  .inner[data-layout='MOBILE'] {
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  .inner[data-layout='MOBILE'] .logoSlot {
    display: flex;
    justify-content: center;
  }

  .mobileMenuSlot {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;

    --n-sheet-animation-duration: 180ms;
  }

  .mobileMenuButton {
    color: inherit;
  }

  .mobileMenuContent {
    padding: var(--n-scale-200) var(--n-scale-500);
    background-color: var(--n-color-surface-neutral-faint);
  }

  .mobileMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .mobileMenuItem + .mobileMenuItem {
    border-top: 1px solid var(--n-color-border-neutral-faint);
  }

  .mobileMenuLink {
    display: block;
    padding: var(--n-scale-400) 0;
    font-size: 1.125rem;
    color: var(--n-typography-heading-color);
    text-decoration: none;
  }

  .mobileMenuLink:hover,
  .mobileMenuLink:focus-visible {
    text-decoration: underline;
  }

  @keyframes drawerSlideOutLeft {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-100%);
    }
  }

  @keyframes drawerSlideInLeft {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  .menuOverlay {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9;
    background-color: var(--n-color-surface-overlay);
    animation: menuOverlayFadeIn 180ms ease forwards;
  }

  /* A fixed header sticks to the scrollport top; the overlay/drawer must be
     viewport-fixed to stay under it, else (absolute, anchored in the scrolling
     page) they scroll off-screen while the header stays put. */
  .menuOverlay[data-fixed] {
    position: fixed;
  }

  .menuOverlayClosing {
    animation: menuOverlayFadeOut 180ms ease forwards;
  }

  @keyframes menuOverlayFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes menuOverlayFadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .menuOverlay,
    .menuOverlayClosing {
      animation: none;
    }
  }

  .portalDrawer {
    position: absolute;
    left: 0;
    bottom: 0;
    width: min(72%, 20rem);
    max-width: 72%;
    z-index: 10;
    background: var(--n-color-surface-neutral-faint);
    overflow-y: auto;
    animation: drawerSlideInLeft 180ms ease forwards;
  }

  .portalDrawer[data-fixed] {
    position: fixed;
  }

  .portalDrawerClosing {
    animation: drawerSlideOutLeft 180ms ease forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    .portalDrawer,
    .portalDrawerClosing {
      animation: none;
    }
  }
}
